import PropTypes from "prop-types"
import { useState, useContext, useEffect } from "react"
import { gql, useLazyQuery, useQuery } from "@apollo/client"
import { Field, useFormikContext } from "formik"
import { LoaderSmall } from "../../Loaders"
import { MultiSelect } from "primereact/multiselect"
import { FormContext } from "../../../context/formContext"
import { UserContext } from "../../../context/userContext"
import { LogDebug, LogError, LogWarning } from "../../../helpers/logger"

import styles from "../../../styles/Form.module.css"

function LocationIds({ field, block, index }) {
  const [activeLocations, setActiveLocations] = useState([])
  const [inactiveLocations, setInactiveLocations] = useState([])
  const [loadingAssignedLocations, setLoadingAssignedLocations] =
    useState(false)

  const form = useContext(FormContext)
  const formik = useFormikContext()
  const userCtx = useContext(UserContext)

  /** Determine if field is disabled */
  let isDisabled = form ? form.blocksSaved?.[block] : false
  isDisabled = field.read_only ? true : isDisabled

  /** Validate required flag */
  const checkIfRequired = () => {
    try {
      return JSON.parse(field?.field_validation?.required?.[0])
    } catch (error) {
      return false
    }
  }

  const isRequired = checkIfRequired()

  // -----------------------------
  // GraphQL Queries
  // -----------------------------
  const GET_LOCATIONS = gql`
    query getLocations($staff_id: ID!, $organization_id: ID!) {
      locations(staff_id: $staff_id, organization_id: $organization_id) {
        key: location_id
        label: location_name
        status: location_status
      }
    }
  `

  const GET_ASSIGNED_LOCATIONS = gql`
    query getAssignedLocations(
      $participant_membership_id: ID!
      $staff_id: ID!
      $organization_id: ID!
    ) {
      locationsByMembershipId(
        participant_membership_id: $participant_membership_id
        staff_id: $staff_id
        organization_id: $organization_id
      ) {
        key: location_id
        label: location_name
        status: location_status
      }
    }
  `

  const [fetchAssignedLocations] = useLazyQuery(GET_ASSIGNED_LOCATIONS, {
    fetchPolicy: "network-only",
  })

  /** Initialize field value safely */
  useEffect(() => {
    form && form.registerLoadingField(field)

    const init = async () => {
      const raw = formik.initialValues?.[field.field_name]

      if (Array.isArray(raw)) {
        formik.setFieldValue(field.field_name, raw)
      } else if (raw && typeof raw === "object") {
        formik.setFieldValue(field.field_name, [raw])
      } else {
        if (!form?.editMode) {
          await applyAssignedLocations()
        } else {
          formik.setFieldValue(field.field_name, [])
        }
      }

      // Notify FormContext that field is loaded
      form && form.completeLoadingField(field)
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps, no-sparse-arrays
  }, [])

  async function applyAssignedLocations() {
    let assignedLocations = []
    setLoadingAssignedLocations(true)

    try {
      // Check if user context has necessary info to fetch assigned locations
      if (
        userCtx?.participant_membership_id &&
        userCtx?.session?.user?.staff_id &&
        userCtx?.session?.user?.organization_id
      ) {
        // Fetch assigned locations for the participant
        const assignedLocationsResult = await fetchAssignedLocations({
          variables: {
            participant_membership_id: userCtx?.participant_membership_id,
            staff_id: userCtx.session.user.staff_id,
            organization_id: userCtx.session.user.organization_id,
          },
        })

        // Set assigned locations if data is returned
        if (assignedLocationsResult?.data?.locationsByMembershipId) {
          assignedLocations =
            assignedLocationsResult.data.locationsByMembershipId.map((loc) => ({
              key: loc.key,
              label: loc.label,
            }))
        }
      }
    } catch (error) {
      LogError("Error fetching assigned locations:", error)
    } finally {
      setLoadingAssignedLocations(false)
    }

    // Apply assigned locations to Formik field value
    formik.setFieldValue(field.field_name, assignedLocations)
  }

  const { loading: loadingLocations } = useQuery(GET_LOCATIONS, {
    fetchPolicy: "network-only",
    variables: {
      staff_id: userCtx?.session?.user?.staff_id,
      organization_id: userCtx?.session?.user?.organization_id,
    },
    skip: !userCtx,
    onCompleted: ({ locations }) => {
      if (!Array.isArray(locations)) {
        setActiveLocations([])
        setInactiveLocations([])
        return
      }

      const activeList =
        locations
          .filter((l) => l.status === "ACTIVE")
          .map((l) => ({ key: l.key, label: l.label })) || []

      const inactiveList =
        locations
          .filter((l) => l.status === "INACTIVE")
          .map((l) => ({ key: l.key, label: l.label })) || []

      setActiveLocations(activeList)
      setInactiveLocations(inactiveList)

      /**
       * 🔥 FIX: Remap Formik selected values using updated dropdown options.
       * If the user had selected a location with an old label or outdated object,
       * replace it with the new one matching the key.
       */
      const current = formik.values[field.field_name]

      if (Array.isArray(current)) {
        const allOptions = [...activeList, ...inactiveList]

        const remapped = current
          .map((item) => {
            const match = allOptions.find((opt) => opt.key === item.key)
            if (!match) {
              LogDebug(
                "LocationIds FIX -> Key exists in form but not in options. Removing:",
                item,
              )
            }
            return match || null
          })
          .filter(Boolean)

        if (JSON.stringify(remapped) !== JSON.stringify(current)) {
          LogDebug(
            "LocationIds FIX -> Updating Formik values to match updated options.",
            { previous: current, updated: remapped },
          )
          formik.setFieldValue(field.field_name, remapped)
        }
      }
    },
    onError: () => {
      setActiveLocations([])
      setInactiveLocations([])
    },
  })

  /** Handle ACTIVE selection */
  const handleSelectLocationIds = (value) => {
    if (value === null) {
      formik.setFieldValue(field.field_name, [])
      return
    }

    const current = Array.isArray(formik.values[field.field_name])
      ? formik.values[field.field_name]
      : []

    const inactiveSelected =
      current.filter((item) =>
        inactiveLocations.find((i) => i.key === item.key),
      ) || []

    const newValue = [...inactiveSelected, ...value]

    formik.setFieldValue(field.field_name, newValue)
  }

  /** Handle INACTIVE deselection */
  const handleDeselectInactiveLocationIds = (value) => {
    if (!Array.isArray(value)) {
      LogError("ERROR → Inactive deselect value is NOT array:", value)
      return
    }

    const current = Array.isArray(formik.values[field.field_name])
      ? formik.values[field.field_name]
      : []

    if (value.length === 0) {
      const activeSelected = current.filter((item) =>
        activeLocations.find((a) => a.key === item.key),
      )

      formik.setFieldValue(field.field_name, activeSelected)
    } else {
      const activeSelected = current.filter((item) =>
        activeLocations.find((a) => a.key === item.key),
      )

      const merged = [...activeSelected, ...value]

      setInactiveLocations(value)
      formik.setFieldValue(field.field_name, merged)
    }
  }

  /** Rendering logic */
  const handleMultiSelectRender = (formikField) => {
    const currentValues = formikField?.value

    if (!Array.isArray(currentValues)) {
      console.error("ERROR → formikField.value is NOT an array:", currentValues)
    }

    const safeValues = Array.isArray(currentValues) ? currentValues : []

    const inactiveSelected = safeValues
      .filter((v) =>
        inactiveLocations.find((inactive) => inactive.key === v.key),
      )
      .map(({ __typename, ...rest }) => rest)

    const activeSelected = safeValues
      .filter((v) => activeLocations.find((active) => active.key === v.key))
      .map(({ __typename, ...rest }) => rest)

    const renderInactiveLocations = () => {
      const shouldDisplay = form?.editMode && inactiveSelected.length > 0

      if (!shouldDisplay) return null

      return (
        <div>
          <label className={styles.label}>Inactive Location ID(s)</label>
          <div className={styles.description}>
            Inactive locations can only be deselected.
          </div>

          <MultiSelect
            placeholder="Unselect only"
            id={field.field_name}
            name={formikField.name}
            value={inactiveSelected}
            onChange={(e) => handleDeselectInactiveLocationIds(e.value)}
            onBlur={(e) => formik.handleBlur(e)}
            disabled={isDisabled}
            options={inactiveLocations}
          />
        </div>
      )
    }

    return (
      <>
        <MultiSelect
          placeholder="Please select..."
          id={field.field_name}
          name={formikField.name}
          value={activeSelected}
          onChange={(e) => handleSelectLocationIds(e.value)}
          onBlur={(e) => formik.handleBlur(e)}
          disabled={isDisabled}
          options={activeLocations}
        />

        {renderInactiveLocations()}
      </>
    )
  }

  if (loadingLocations || loadingAssignedLocations) return <LoaderSmall />

  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>

      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}

      <div className={styles.value}>
        <Field name={field.field_name}>
          {({ field: formikField, meta }) => (
            <>
              {handleMultiSelectRender(formikField)}

              {meta.touched && meta.error && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
            </>
          )}
        </Field>
      </div>
    </div>
  )
}

LocationIds.propTypes = {
  field: PropTypes.object.isRequired,
  block: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
}

export default LocationIds
